/**
 * Lexer for the Nova query language.
 *
 * Grammar, informally:
 *
 *   query   := term*
 *   term    := "-"? (field ":" op? value | bare)
 *   value   := number | range | quoted | word | wildcard
 *   range   := number ("-" | "..") number
 *   op      := ">" | ">=" | "<" | "<=" | "="
 *
 * Examples:
 *   artist:burna bpm:100-115 key:8A energy:>0.7 -genre:gospel
 *   genre:amapiano/*  "log drum"  year:2020..2024  reviewed:no
 *
 * Design notes:
 *
 * Tokenising is deliberately separate from parsing so the UI can render what it
 * understood -- and highlight exactly which token failed -- rather than silently
 * falling back to a substring match on the whole string. Silent fallback is the
 * worst outcome: the user believes they filtered when they did not.
 *
 * Quotes are respected so multi-word values work: artist:"burna boy".
 */

export type TokenKind = "field" | "bare" | "error";

export interface RawToken {
  kind: TokenKind;
  /** Character offsets into the source, for inline error highlighting. */
  start: number;
  end: number;
  /** Original source slice. */
  text: string;
  negated: boolean;
  /** Field name, lowercased. Absent for bare terms. */
  field?: string;
  /** Comparison operator if one was written. */
  op?: string;
  /** Raw value text, quotes stripped. */
  value?: string;
  /** Whether the value was quoted, which suppresses range/number parsing. */
  quoted?: boolean;
  message?: string;
}

const OPERATOR_CHARS = new Set([">", "<", "="]);

function isSpace(c: string): boolean {
  return c === " " || c === "\t" || c === "\n" || c === "\r";
}

/** Split a query string into tokens, preserving source offsets. */
export function tokenize(input: string): RawToken[] {
  const tokens: RawToken[] = [];
  let i = 0;

  while (i < input.length) {
    while (i < input.length && isSpace(input[i])) i++;
    if (i >= input.length) break;

    const start = i;
    let negated = false;

    if (input[i] === "-" && i + 1 < input.length && !isSpace(input[i + 1])) {
      negated = true;
      i++;
    }

    // Read up to ':' or whitespace to decide field vs bare.
    let head = "";
    while (i < input.length && !isSpace(input[i]) && input[i] !== ":") {
      if (input[i] === '"') break;
      head += input[i++];
    }

    // Bare quoted phrase: "log drum"
    if (input[i] === '"' && head === "") {
      const { value, next, closed } = readQuoted(input, i);
      tokens.push({
        kind: closed ? "bare" : "error",
        start,
        end: next,
        text: input.slice(start, next),
        negated,
        value,
        quoted: true,
        message: closed ? undefined : "unclosed quote",
      });
      i = next;
      continue;
    }

    if (input[i] !== ":") {
      // Bare word.
      tokens.push({
        kind: "bare",
        start,
        end: i,
        text: input.slice(start, i),
        negated,
        value: head,
      });
      continue;
    }

    // Field term: consume ':'
    i++;
    const field = head.toLowerCase();

    let op = "";
    while (i < input.length && OPERATOR_CHARS.has(input[i])) op += input[i++];

    let value = "";
    let quoted = false;
    let unclosed = false;

    if (input[i] === '"') {
      const r = readQuoted(input, i);
      value = r.value;
      quoted = true;
      unclosed = !r.closed;
      i = r.next;
    } else {
      while (i < input.length && !isSpace(input[i])) value += input[i++];
    }

    if (field === "") {
      tokens.push({
        kind: "error",
        start,
        end: i,
        text: input.slice(start, i),
        negated,
        message: "missing field name before ':'",
      });
      continue;
    }

    if (value === "" && !quoted) {
      tokens.push({
        kind: "error",
        start,
        end: i,
        text: input.slice(start, i),
        negated,
        field,
        op: op || undefined,
        message: `'${field}:' has no value`,
      });
      continue;
    }

    tokens.push({
      kind: unclosed ? "error" : "field",
      start,
      end: i,
      text: input.slice(start, i),
      negated,
      field,
      op: op || undefined,
      value,
      quoted,
      message: unclosed ? "unclosed quote" : undefined,
    });
  }

  return tokens;
}

function readQuoted(
  input: string,
  at: number
): { value: string; next: number; closed: boolean } {
  let i = at + 1;
  let value = "";
  while (i < input.length && input[i] !== '"') {
    if (input[i] === "\\" && i + 1 < input.length) i++;
    value += input[i++];
  }
  const closed = input[i] === '"';
  return { value, next: closed ? i + 1 : i, closed };
}
